import type { RefObject } from 'react'
import type { AgGridReact } from 'ag-grid-react'
import { ITEM_STATUS_OPTIONS } from './StatusCell'
import type { RequestItemStatus } from '../../../shared/api/types'
import { assigneeLabel, useAssignableBuyers } from '../hooks/useAssignableBuyers'

type Props = {
  totalItems: number
  visibleCount: number
  itemSearch: string
  setItemSearch: (v: string) => void
  itemStatusFilter: RequestItemStatus | ''
  setItemStatusFilter: (v: RequestItemStatus | '') => void
  isAdmin: boolean
  userRole: string | undefined
  bulkPriorityId: string
  setBulkPriorityId: (v: string) => void
  isBulkPriority: boolean
  onBulkPriority: () => void
  bulkAssignId: string
  setBulkAssignId: (v: string) => void
  isBulkAssigning: boolean
  onBulkAssign: () => void
  gridRef: RefObject<AgGridReact | null>
  onResetCols: () => void
}

export function ItemsToolbar({
  totalItems,
  visibleCount,
  itemSearch,
  setItemSearch,
  itemStatusFilter,
  setItemStatusFilter,
  isAdmin,
  userRole,
  bulkPriorityId,
  setBulkPriorityId,
  isBulkPriority,
  onBulkPriority,
  bulkAssignId,
  setBulkAssignId,
  isBulkAssigning,
  onBulkAssign,
  gridRef,
  onResetCols,
}: Props) {
  const buyers = useAssignableBuyers()
  return (
    <div className="px-4 py-2.5 border-b border-gray-100 flex flex-wrap items-center gap-2">
      <h2 className="text-sm font-medium text-gray-700 mr-1">Позиции ({totalItems})</h2>
      <input
        type="text"
        placeholder="Поиск арт./бренда..."
        value={itemSearch}
        onChange={(e) => setItemSearch(e.target.value)}
        className="px-2 py-1 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-brand-500 w-36"
      />
      <select
        value={itemStatusFilter}
        onChange={(e) => setItemStatusFilter(e.target.value as RequestItemStatus | '')}
        className="px-2 py-1 border border-gray-200 rounded-lg text-xs bg-white focus:outline-none focus:ring-1 focus:ring-brand-500"
      >
        {ITEM_STATUS_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {(itemSearch || itemStatusFilter) && (
        <button
          onClick={() => {
            setItemSearch('')
            setItemStatusFilter('')
          }}
          className="text-xs text-gray-400 hover:text-gray-600"
        >
          × Сбросить
        </button>
      )}
      {/* Приоритет для всех — для ADMIN и MANAGER */}
      {(isAdmin || userRole === 'MANAGER' || userRole === 'ROP') && (
        <div className="flex items-center gap-1">
          <select
            value={bulkPriorityId}
            onChange={(e) => setBulkPriorityId(e.target.value)}
            className="px-2 py-1 border border-gray-200 rounded-lg text-xs bg-white focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="">Приоритет всем...</option>
            <option value="HIGH">🔴 Высокий</option>
            <option value="MEDIUM">🟡 Средний</option>
            <option value="LOW">⚪ Базовый</option>
          </select>
          {bulkPriorityId && (
            <button
              onClick={onBulkPriority}
              disabled={isBulkPriority}
              className="px-2.5 py-1 text-xs bg-gray-700 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 whitespace-nowrap"
            >
              {isBulkPriority ? '...' : 'Применить'}
            </button>
          )}
        </div>
      )}
      {/* Назначить всем — ADMIN, MANAGER, ROP и РОЗ (себя и свою команду, №50) */}
      {(isAdmin || userRole === 'MANAGER' || userRole === 'ROP' || userRole === 'ROS') &&
        buyers.length > 0 && (
          <div className="flex items-center gap-1 ml-1">
            <select
              value={bulkAssignId}
              onChange={(e) => setBulkAssignId(e.target.value)}
              className="px-2 py-1 border border-gray-200 rounded-lg text-xs bg-white focus:outline-none focus:ring-1 focus:ring-brand-500"
            >
              <option value="">Назначить всем...</option>
              {buyers.map((pu) => (
                <option key={pu.id} value={pu.id}>
                  {assigneeLabel(pu)}
                </option>
              ))}
            </select>
            {bulkAssignId && (
              <button
                onClick={onBulkAssign}
                disabled={isBulkAssigning}
                className="px-2.5 py-1 text-xs bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 whitespace-nowrap"
              >
                {isBulkAssigning ? '...' : 'Применить'}
              </button>
            )}
          </div>
        )}

      <span className="ml-auto text-xs text-gray-400">
        {visibleCount !== totalItems && `${visibleCount} из `}
        {totalItems} поз.
      </span>
      <button
        onClick={() => {
          onResetCols()
          gridRef.current?.api.resetColumnState()
        }}
        className="text-xs text-gray-400 hover:text-gray-600"
        title="Сбросить порядок и ширину столбцов"
      >
        Сброс столбцов
      </button>
    </div>
  )
}
